import type { ManagerGender } from '@/schemas/managerRegisterPairSchema';

export const CATEGORY_RANKING_PERIOD = {
  ALL_TIME: 'ALL_TIME',
  THIS_MONTH: 'THIS_MONTH',
  THIS_YEAR: 'THIS_YEAR',
} as const;

export type CategoryRankingPeriod =
  (typeof CATEGORY_RANKING_PERIOD)[keyof typeof CATEGORY_RANKING_PERIOD];

export const CATEGORY_RANKING_PERIOD_OPTIONS: {
  label: string;
  value: CategoryRankingPeriod;
}[] = [
  { label: 'Histórico', value: CATEGORY_RANKING_PERIOD.ALL_TIME },
  { label: 'Este mes', value: CATEGORY_RANKING_PERIOD.THIS_MONTH },
  { label: 'Este año', value: CATEGORY_RANKING_PERIOD.THIS_YEAR },
];

export interface CategoryRankingEntry {
  email: string;
  playerName: string;
  points: number;
  position: number;
  profileImageUrl?: string;
}

export interface ClubCategoryRankingParams {
  categoryId: number;
  gender?: string;
  page?: number;
  period: CategoryRankingPeriod;
  query?: string;
  size?: number;
  sport: string;
}

export const CATEGORY_RANKING_STAGE = {
  GROUP_OUT: 'GROUP_OUT',
  ROUND64: 'ROUND64',
  ROUND32: 'ROUND32',
  ROUND16: 'ROUND16',
  QUARTERFINAL: 'QUARTERFINAL',
  SEMIFINAL: 'SEMIFINAL',
  FINAL: 'FINAL',
  CHAMPION: 'CHAMPION',
} as const;

export type CategoryRankingStage =
  (typeof CATEGORY_RANKING_STAGE)[keyof typeof CATEGORY_RANKING_STAGE];

export const RANKING_POINTS_SOURCE = {
  TOURNAMENT: 'TOURNAMENT',
  IMPORTED: 'IMPORTED',
} as const;

export type RankingPointsSource =
  (typeof RANKING_POINTS_SOURCE)[keyof typeof RANKING_POINTS_SOURCE];

export interface CategoryRankingEntryDetail {
  earnedAt: string;
  points: number;
  stage: CategoryRankingStage;
  /** Absent for imported entries: they have no tournament behind them. */
  tournamentId?: string;
  /** For imported entries the backend fills this with the batch name. */
  tournamentName?: string;
  source?: RankingPointsSource;
}

export interface PlayerCategoryRankingEntriesParams {
  categoryId: number;
  email: string;
  page?: number;
  period: CategoryRankingPeriod;
  size?: number;
  sport: string;
}

export interface CategoryRankingPointsRule {
  points: number;
  stage: CategoryRankingStage;
}

/**
 * Stages offered by the import grid, best to worst. Labels via getCategoryRankingStageLabel.
 *
 * Derived from CATEGORY_RANKING_STAGE — declared worst to best — rather than re-listed, so a
 * stage added to the enum cannot silently go missing from the import dropdown.
 */
export const CATEGORY_RANKING_STAGE_OPTIONS: CategoryRankingStage[] =
  Object.values(CATEGORY_RANKING_STAGE).reverse();

export const RANKING_IMPORT_ROW_STATUS = {
  EXISTING_USER: 'EXISTING_USER',
  NEW_USER: 'NEW_USER',
} as const;

export type RankingImportRowStatus =
  (typeof RANKING_IMPORT_ROW_STATUS)[keyof typeof RANKING_IMPORT_ROW_STATUS];

/** Mirrors RankingImportService.MAX_ROWS: over this the backend rejects the whole batch with 400. */
export const RANKING_IMPORT_MAX_ROWS = 500;

/**
 * Subset of the backend's RegisterUserDTO the import sends. Only used when the email has no
 * account; ignored for existing players.
 */
export interface RankingImportNewUser {
  name: string;
  /**
   * The backend matches this against its GenderEnum and errors the row on anything else, so it
   * reuses the same type the manual player creation flow sends (see ManagerNewUserPayload).
   */
  gender?: ManagerGender;
  /**
   * Full ISO-8601 with offset ("1990-04-20T00:00:00Z"). The backend field is an OffsetDateTime
   * and a bare "1990-04-20" fails Jackson deserialization, 400ing the whole batch.
   * Build it with `toBackendBirthdate`.
   */
  birthdate?: string;
}

export interface RankingImportRowInput {
  email: string;
  newUser?: RankingImportNewUser;
  categoryId: number;
  stage: CategoryRankingStage;
}

export interface RankingImportForm {
  /** Sport uid as returned by useSportsStore ("PADEL"): it doubles as the backend enum name. */
  sport: string;
  tournamentName: string;
  /** "YYYY-MM-DD" — every entry of the batch gets this earnedAt at 00:00. */
  earnedAt: string;
  rows: RankingImportRowInput[];
}

export interface RankingImportRowResult {
  /** 0-based index into the submitted rows. */
  index: number;
  /** The raw email as submitted, not normalized. */
  email?: string;
  status?: RankingImportRowStatus;
  points?: number;
  warning?: string;
  error?: string;
}

export interface RankingImportResult {
  /** Null on dry-run and whenever any row has an error — in both cases nothing was persisted. */
  batchId?: string | null;
  dryRun: boolean;
  totalRows: number;
  validRows: number;
  /** Lowercased emails that would be (or were) created. */
  usersToCreate: string[];
  totalPoints: number;
  rows: RankingImportRowResult[];
}

export interface RankingImportBatch {
  id: string;
  tournamentName: string;
  /** Sport uid ("PADEL"), not a label. */
  sport: string;
  /** LocalDateTime without offset: "2026-01-15T00:00:00". */
  earnedAt: string;
  createdAt: string;
  importedByName?: string;
  rowsCount: number;
  createdUsersCount: number;
  totalPoints: number;
}
